import axios from "axios";
import Cookies from "js-cookie";
import {
  baseKpiToAdminUpsertPayload,
  normalizeBaseKpi,
  normalizeKpiCatalogResponse,
  normalizeSelectionVariant,
  normalizeSelectionVariantsResponse,
  variantToAdminUpsertPayload,
} from "../kpiCatalogUtils";
import { KPI_CATALOG_SEED } from "../kpiCatalogSeed";

const API_ROOT = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const CATALOG_URL = `${API_ROOT}/api/analytics/kpi-catalog`;
const DEFINITIONS_URL = `${API_ROOT}/api/analytics/kpi-definitions`;
const VARIANTS_URL = `${API_ROOT}/api/analytics/kpi-selection-variants`;

const authHeaders = () => {
  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(officeCode ? { "Office-Code": officeCode } : {}),
  };
};

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeaders(),
});

function notifyCatalogUpdated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("kpi-catalog-updated"));
  }
}

export function extractKpiApiError(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.content && typeof data.content === "string") return data.content;
  if (data?.message) return data.message;
  if (data?.error && typeof data.error === "string") return data.error;
  return err?.message || fallback;
}

/** True when analytics KPI admin APIs are not deployed on the current backend. */
function isKpiAnalyticsApiUnavailable(err) {
  const status = err?.response?.status;
  if ([404, 501, 502, 503].includes(status)) return true;
  const message = extractKpiApiError(err, "").toLowerCase();
  return (
    message.includes("no handler") ||
    message.includes("not found") ||
    message.includes("unknown url") ||
    message.includes("cannot get ")
  );
}

function seededKpiCatalog() {
  return normalizeKpiCatalogResponse(KPI_CATALOG_SEED);
}

function flattenVariantsFromCatalog(catalog) {
  return catalog.flatMap((base) =>
    (base.selectionVariants || []).map((v) => ({
      ...v,
      parentKpiCode: v.parentKpiCode || base.kpiCode,
      parentDisplayName: base.displayName,
      parentEnabled: base.enabled,
    }))
  );
}

function mergePatchIntoBase(existing, patch) {
  if (!patch || typeof patch !== "object") return existing;
  return normalizeBaseKpi({
    ...existing,
    ...(patch.kpi_code || patch.kpiCode ? { kpi_code: patch.kpi_code ?? patch.kpiCode } : {}),
    ...(patch.display_name != null || patch.displayName != null
      ? { display_name: patch.display_name ?? patch.displayName }
      : {}),
    ...(patch.description != null ? { description: patch.description } : {}),
    ...(patch.category != null ? { category: patch.category } : {}),
    ...(patch.enabled != null ? { enabled: patch.enabled } : {}),
    ...(patch.supported_dimensions || patch.supportedDimensions
      ? { supported_dimensions: patch.supported_dimensions ?? patch.supportedDimensions }
      : {}),
    ...(patch.measures ? { measures: patch.measures } : {}),
    selection_variants: existing.selectionVariants,
  });
}

/** GET /api/analytics/kpi-catalog */
export async function fetchKpiCatalog(params = {}) {
  try {
    const { data } = await axios.get(CATALOG_URL, {
      headers: authHeaders(),
      params,
    });
    const normalized = normalizeKpiCatalogResponse(data);
    if (normalized.length) return normalized;
  } catch (err) {
    if (!isKpiAnalyticsApiUnavailable(err)) throw err;
    console.warn(
      "KPI catalog API unavailable; using seeded catalog until /api/analytics/kpi-catalog is deployed.",
      err
    );
  }
  return seededKpiCatalog();
}

/** GET /api/analytics/kpi-selection-variants */
export async function fetchKpiSelectionVariants(params = {}) {
  try {
    const { data } = await axios.get(VARIANTS_URL, {
      headers: authHeaders(),
      params,
    });
    const normalized = normalizeSelectionVariantsResponse(data);
    if (normalized.length) return normalized;
  } catch (err) {
    if (!isKpiAnalyticsApiUnavailable(err)) throw err;
  }
  return flattenVariantsFromCatalog(await fetchKpiCatalog(params));
}

/** GET /api/analytics/kpi-definitions/{kpiCode} */
export async function fetchBaseKpiDefinition(kpiCode) {
  const code = String(kpiCode || "").trim();
  const { data } = await axios.get(`${DEFINITIONS_URL}/${encodeURIComponent(code)}`, {
    headers: authHeaders(),
  });
  return normalizeBaseKpi(data);
}

/** POST /api/analytics/kpi-definitions */
export async function createBaseKpi(payload) {
  const normalized = normalizeBaseKpi(payload) ?? payload;
  const body = baseKpiToAdminUpsertPayload(normalized);
  try {
    const { data } = await axios.post(DEFINITIONS_URL, body, { headers: jsonHeaders() });
    notifyCatalogUpdated();
    return normalizeBaseKpi(data) ?? normalized;
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to create base KPI."));
  }
}

/** PUT /api/analytics/kpi-definitions/{kpiCode} */
export async function updateBaseKpi(kpiCode, patch) {
  const code = String(kpiCode || "").trim();
  if (!code) throw new Error("KPI code is required.");

  const existing = await fetchBaseKpiDefinition(code);
  const merged = mergePatchIntoBase(existing, patch);
  const body = baseKpiToAdminUpsertPayload(merged, { includeKpiCode: false });

  try {
    const { data } = await axios.put(`${DEFINITIONS_URL}/${encodeURIComponent(code)}`, body, {
      headers: jsonHeaders(),
    });
    notifyCatalogUpdated();
    return normalizeBaseKpi(data) ?? merged;
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to update base KPI."));
  }
}

/** POST /api/analytics/kpi-selection-variants */
export async function createSelectionVariant(kpiCode, payload) {
  const parentCode = String(kpiCode || "").trim();
  const body = variantToAdminUpsertPayload(payload, parentCode);

  try {
    const { data } = await axios.post(VARIANTS_URL, body, { headers: jsonHeaders() });
    notifyCatalogUpdated();
    return normalizeSelectionVariant(data) ?? normalizeSelectionVariant(body);
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to create selection variant."));
  }
}

/** PUT /api/analytics/kpi-selection-variants/{variantCode} */
export async function updateSelectionVariant(variantCode, payload) {
  const code = String(variantCode || "").trim();
  const body = variantToAdminUpsertPayload(payload, payload?.parent_kpi_code ?? payload?.parentKpiCode);

  try {
    const { data } = await axios.put(`${VARIANTS_URL}/${encodeURIComponent(code)}`, body, {
      headers: jsonHeaders(),
    });
    notifyCatalogUpdated();
    return normalizeSelectionVariant(data) ?? normalizeSelectionVariant(body);
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to update selection variant."));
  }
}

/** DELETE /api/analytics/kpi-selection-variants/{variantCode} */
export async function deleteSelectionVariant(variantCode) {
  const code = String(variantCode || "").trim();
  if (!code) throw new Error("Variant code is required.");

  try {
    await axios.delete(`${VARIANTS_URL}/${encodeURIComponent(code)}`, {
      headers: authHeaders(),
    });
    notifyCatalogUpdated();
  } catch (err) {
    throw new Error(extractKpiApiError(err, "Failed to delete selection variant."));
  }
}

export const kpiCatalogApi = {
  fetchKpiCatalog,
  fetchKpiSelectionVariants,
  fetchBaseKpiDefinition,
  createBaseKpi,
  updateBaseKpi,
  createSelectionVariant,
  updateSelectionVariant,
  deleteSelectionVariant,
};

/** @deprecated use kpiCatalogApi */
export const kpiPlatformApi = kpiCatalogApi;
